import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FormFieldSet } from "@progress/kendo-react-form";
import { hyperlinkOpenIcon, downloadIcon } from "@progress/kendo-svg-icons";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
  ContentHeaderBackNavigation,
  ContentHeaderActions,
} from "@excent-one/react/layout/content";
import { FormLayout, FormSection, SearchFormLayout } from "@excent-one/react";
import { DataGrid, DataGridColumnProps } from "@excent-one/react/data-grid";
import {
  ControlledDatePicker,
  ControlledComboBox,
  ControlledInput,
} from "@excent-one/react/form/inputs";
import { Button } from "@progress/kendo-react-all";
import { notification } from "@excent-one/react/notification";
import CreateWithTypeButton from "@phrmpc/shared/components/CreateWithTypeButton";
import {
  searchDailyActivityMonitoringLogHeaders,
  createDailyActivityMonitoringLogHeader,
} from "@phrmpc/infrastructure/phrmpc/api/daily-activity-monitoring-log-headers";
import { getAllDailyActivityMonitoringLogStatuses } from "@phrmpc/infrastructure/phrmpc/api/daily-activity-monitoring-log-statuses";
import {
  type DailyActivityMonitoringLogHeaderSummaryDto,
  type BranchDto,
  MyCalendarAssignmentDto,
} from "@phrmpc/infrastructure/phrmpc/models";
import { FilterLogic, FilterOperator } from "@phrmpc/shared";
import { DateTime } from "luxon";
import {
  getAllBranches,
  getCoordinators,
  getCoordinatorMembers,
  getMyDetails,
  getMyCalendarBranchMyCalendar,
} from "@phrmpc/infrastructure/phrmpc";
import { usePagedGrid } from "@phrmpc/shared/hooks/usePagedGrid";
import { useAuthContext } from "@excent-one/auth";

const defaultSearchValues: DailyActivityMonitoringLogHeaderSummaryDto = {
  date: undefined,
  coordinatorId: undefined,
  branchId: undefined,
  statusId: undefined,
};

const buildFilter = (values: DailyActivityMonitoringLogHeaderSummaryDto) => {
  const filters: { field: string; operator: number; value: unknown }[] = [];
  if (values.date) {
    filters.push({
      field: "date",
      operator: FilterOperator.Equals,
      value: values.date,
    });
  }
  if (values.coordinatorId != null) {
    filters.push({
      field: "coordinatorId",
      operator: FilterOperator.Equals,
      value: values.coordinatorId,
    });
  }
  if (values.branchId != null) {
    filters.push({
      field: "branchId",
      operator: FilterOperator.Equals,
      value: values.branchId,
    });
  }
  if (values.statusId != null) {
    filters.push({
      field: "statusId",
      operator: FilterOperator.Equals,
      value: values.statusId,
    });
  }
  if (filters.length === 0) return null;
  return { logic: FilterLogic.And, filters } as const;
};

const COLUMNS: DataGridColumnProps<DailyActivityMonitoringLogHeaderSummaryDto>[] =
  [
    { field: "logId", title: "Log ID", readOnly: true },
    {
      field: "date",
      title: "Date",
      readOnly: true,
      cells: {
        data: (props: any) => (
          <td>
            {props.dataItem.date
              ? DateTime.fromJSDate(props.dataItem.date).toFormat("MM/dd/yyyy")
              : "-"}
          </td>
        ),
      },
    },
    { field: "coordinatorName", title: "Coordinator Name", readOnly: true },
    { field: "branchName", title: "Site", readOnly: true },
    { field: "statusName", title: "Status", readOnly: true },
  ];

const DailyActivityMonitoringLogSearchForm = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [{ currentUser }, { isInAnyRole }] = useAuthContext();
  const isUserAreaHead = isInAnyRole("Area Head");
  const isUserCoordinator = isInAnyRole("Coordinator");

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const { control, getValues, reset } =
    useForm<DailyActivityMonitoringLogHeaderSummaryDto>({
      defaultValues: defaultSearchValues,
    });

  const [appliedFilters, setAppliedFilters] =
    useState<DailyActivityMonitoringLogHeaderSummaryDto>(defaultSearchValues);
  const { skip, pageSize, pageNumber, pageable, onPageChange, resetPaging } =
    usePagedGrid();

  const { data: branchesData } = useQuery({
    queryKey: ["branches"],
    queryFn: () => getAllBranches(),
  });
  const branches = branchesData ?? [];

  const { data: myBranchAssignments = [] } = useQuery({
    queryKey: ["my-calendar-branches", DateTime.now().toFormat("yyyy-MM-dd")],
    queryFn: () =>
      getMyCalendarBranchMyCalendar() as unknown as Promise<
        MyCalendarAssignmentDto[]
      >,
  });

  const assignedIds = new Set(myBranchAssignments.map((a) => a.branchId));
  const myBranches = branches.filter(
    (b) => b.id != null && assignedIds.has(b.id),
  );

  const { data: statusOptions } = useQuery({
    queryKey: ["daml-statuses"],
    queryFn: () => getAllDailyActivityMonitoringLogStatuses(),
  });

  const { data: coordinators } = useQuery({
    queryKey: ["coordinator-members", myDetails?.id],
    queryFn: async () => {
      const result = await getCoordinatorMembers(myDetails!.id!);
      return result.items ?? [];
    },
    enabled: isUserAreaHead && Boolean(myDetails?.id),
  });

  const { data: fallbackCoordinators } = useQuery({
    queryKey: ["coordinators"],
    queryFn: async () => {
      const result = await getCoordinators({
        payload: { pageNumber: 1, pageSize: 500 },
      });
      return result.items ?? [];
    },
    enabled: !isUserAreaHead && !isUserCoordinator,
  });

  const coordinatorOptions = isUserCoordinator
    ? []
    : isUserAreaHead
      ? (coordinators ?? [])
      : (fallbackCoordinators ?? []);

  const { data: pagedResult, isLoading } = useQuery({
    queryKey: ["daml-headers", appliedFilters, skip, pageSize],
    queryFn: () =>
      searchDailyActivityMonitoringLogHeaders({
        payload: {
          pageNumber,
          pageSize,
          filter: buildFilter(appliedFilters) as any,
        },
      }),
  });

  useEffect(() => {
    if (!isUserCoordinator || !myDetails) return;
    const scopedValues: DailyActivityMonitoringLogHeaderSummaryDto = {
      ...defaultSearchValues,
      coordinatorId: myDetails.id,
    };
    reset(scopedValues);
    setAppliedFilters(scopedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserCoordinator, myDetails]);

  const handleSearch = () => {
    const values = getValues();
    if (buildFilter(values) === null) {
      notification.error("Please specify at least one search criterion.");
      return;
    }
    resetPaging();
    setAppliedFilters(values);
  };

  const handleClear = () => {
    reset(defaultSearchValues);
    resetPaging();
    setAppliedFilters(defaultSearchValues);
  };

  const onExport = () => {
    notification.error("Export is not yet available.");
  };

  const handleCreate = async (branch: BranchDto) => {
    if (branch.id == null) return;
    setCreating(true);
    try {
      const created = await createDailyActivityMonitoringLogHeader({
        payload: { branchId: branch.id },
      });
      navigate({
        to: "/deployment/daily-activity-monitoring-logs/$id/edit",
        params: { id: created.id! },
      });
    } catch (e) {
      console.error(e);
      notification.error("Unable to create record. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle title="Search Daily Activity Monitoring Log" />
        <ContentHeaderBreadcrumbs />
        <SearchFormLayout
          buttonProps={{
            search: { onClick: handleSearch },
            clear: { onClick: handleClear },
          }}
        >
          <FormFieldSet cols={2}>
            <ControlledDatePicker control={control} name="date" label="Date" />
            {coordinatorOptions.length ? (
              <ControlledComboBox
                control={control}
                name="coordinatorId"
                label="Coordinator Name"
                data={coordinatorOptions}
                dataItemKey="id"
                textField="fieldName"
                wrapperProps={{ className: "basic-search-field" }}
              />
            ) : (
              isUserCoordinator && (
                <ControlledInput
                  control={control}
                  name="coordinatorName"
                  label="Coordinator Name"
                  wrapperProps={{ className: "basic-search-field" }}
                  readOnly
                />
              )
            )}
            {branchesData && (
              <ControlledComboBox
                control={control}
                name="branchId"
                label="Site/Branch"
                data={branchesData}
                dataItemKey="id"
                textField="name"
              />
            )}
            {statusOptions && (
              <ControlledComboBox
                control={control}
                name="statusId"
                label="Status"
                data={statusOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
          </FormFieldSet>
        </SearchFormLayout>
        <ContentHeaderActions>
          <CreateWithTypeButton
            items={myBranches}
            label="Create Daily Activity Monitoring Log"
            onSelect={handleCreate}
            disabled={creating}
          />
          <Button
            fillMode="outline"
            themeColor="base"
            svgIcon={downloadIcon}
            onClick={onExport}
          >
            Export
          </Button>
        </ContentHeaderActions>
      </ContentHeader>

      <FormLayout>
        <FormSection title="Results">
          <DataGrid
            data={pagedResult?.items ?? []}
            dataItemKey="id"
            columns={COLUMNS}
            canAdd={false}
            hideRowCommandToggle={false}
            commands={[
              {
                command: "view",
                text: "View",
                svgIcon: hyperlinkOpenIcon,
              },
            ]}
            onCommandClicked={{
              view: (e) => {
                if (e.dataItem == null || !e.dataItem.id) return;
                navigate({
                  to: "/deployment/daily-activity-monitoring-logs/$id/display",
                  params: { id: e.dataItem.id },
                });
              },
            }}
            pageable={pageable}
            skip={skip}
            take={pageSize}
            total={pagedResult?.totalItems ?? 0}
            onPageChange={onPageChange}
            noRecordsMessage="No matching monitoring log records found."
            style={{ maxHeight: "500px" }}
            scrollable="virtual"
            loading={isLoading}
          />
        </FormSection>
      </FormLayout>
    </ContentLayout>
  );
};

export default DailyActivityMonitoringLogSearchForm;
